/** Public nomination form for CCS Spotlight. */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "./spotlightUtils";

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#2A1810]/20 rounded-none focus:border-[#C28A2E] focus:outline-none focus:ring-0 px-0 py-2.5 text-[#2A1810] placeholder:text-[#2A1810]/35 text-sm";

const Label = ({ children }) => (
  <label className="block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#6B5944] mb-1">{children}</label>
);

export const NominationDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    nominator_name: "", nominator_email: "", nominee_name: "",
    nominee_organization: "", category: "Customer Service", story: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nominator_name || !form.nominator_email || !form.nominee_name || form.story.length < 10) {
      toast.error("Please fill in your name, email, the nominee and their story.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/spotlight-nominations", form);
      toast.success("Thank you — your nomination has been received.");
      onOpenChange(false);
      setForm({ nominator_name: "", nominator_email: "", nominee_name: "", nominee_organization: "", category: "Customer Service", story: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FDFBF5] border-[#EBE3D2] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="nomination-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-[#2A1810]">Submit a Nomination</DialogTitle>
          <DialogDescription className="text-[#6B5944]">
            Know someone who demonstrates excellence in action? Tell us their story.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label>Your name *</Label>
              <input className={inputCls} value={form.nominator_name} onChange={(e) => setF("nominator_name", e.target.value)} data-testid="nomination-nominator-name" />
            </div>
            <div>
              <Label>Your email *</Label>
              <input type="email" className={inputCls} value={form.nominator_email} onChange={(e) => setF("nominator_email", e.target.value)} data-testid="nomination-nominator-email" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label>Nominee's name *</Label>
              <input className={inputCls} value={form.nominee_name} onChange={(e) => setF("nominee_name", e.target.value)} data-testid="nomination-nominee-name" />
            </div>
            <div>
              <Label>Company / organization</Label>
              <input className={inputCls} value={form.nominee_organization} onChange={(e) => setF("nominee_organization", e.target.value)} data-testid="nomination-nominee-org" />
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setF("category", v)}>
              <SelectTrigger className="bg-white border-[#EBE3D2]" data-testid="nomination-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>What did they do? *</Label>
            <textarea
              rows={5}
              className={`${inputCls} resize-none border bg-white/60 border-[#EBE3D2] rounded-lg px-3 py-2.5 focus:border-[#C28A2E]`}
              placeholder="Describe the moment of excellence you witnessed…"
              value={form.story}
              onChange={(e) => setF("story", e.target.value)}
              data-testid="nomination-story"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2A1810] text-[#FDFBF5] py-3.5 rounded-full text-sm font-semibold tracking-wide hover:bg-[#5D3A1F] transition-colors duration-300 disabled:opacity-60"
            data-testid="nomination-submit-button"
          >
            {submitting ? "Submitting…" : "Submit Nomination"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NominationDialog;
